import { inject, Injectable } from "@angular/core";
import { ActivityService } from "./activity.service";
import { Router } from "@angular/router";
import {
  BASE_ENVIRONMENT,
  convertMinutesToMilliseconds,
  IdlePeriod,
} from "blu-utilities";
import { AccountService } from "src/app/modules/authentication/services/account.service";
import { AppService } from "src/services/app.service";
import { LogoutService } from "./logout.service";
import { ErrorMessages } from "../models/error";
import { AppNotificationService } from "./app-notification.service";

/**
 * Applies the timeout policy using the timestamp maintained by ActivityService.
 *
 * ActivityService answers "when was the user last active?".
 * SessionTimeoutService answers "should inactivity force a logout now?".
 */
@Injectable({ providedIn: "root" })
export class SessionTimeoutService {
  private readonly appNotificationService = inject(AppNotificationService);
  private readonly activityService = inject(ActivityService);
  private readonly accountService = inject(AccountService);
  private readonly logoutService = inject(LogoutService);
  private readonly environment = inject(BASE_ENVIRONMENT);

  readonly idleAmount = IdlePeriod.FIFTEEN_MINUTES;
  private readonly appService = inject(AppService);
  private readonly router = inject(Router);

  idlePeriod = this.environment.idlePeriod ?? IdlePeriod.FIFTEEN_MINUTES;
  private readonly idleAmountInMs = convertMinutesToMilliseconds(
    this.idleAmount,
  );
  private readonly foregroundCheckIntervalInMs = Math.min(
    this.idleAmountInMs,
    30_000,
  );
  private foregroundTimerId: number | null = null;
  private readonly safeRoutes: string[] = ["/login"];
  private isInitialized = false;

  /**
   * Starts the visibility/focus listeners that keep session expiration aligned
   * with real browser lifecycle events.
   */
  initializeResumeChecks(): void {
    if (this.isInitialized) {
      return;
    }

    this.isInitialized = true;

    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") {
        this.checkAndLogoutIfExpired();
        this.startForegroundGuard();
      } else {
        this.stopForegroundGuard();
      }
    });

    window.addEventListener("focus", () => {
      this.checkAndLogoutIfExpired();
      this.startForegroundGuard();
    });

    if (document.visibilityState === "visible") {
      this.startForegroundGuard();
    }
  }

  /**
   * While the tab is visible, poll at a short cadence so the logout happens
   * close to the actual idle threshold rather than the next long interval.
   */
  private startForegroundGuard(): void {
    if (this.foregroundTimerId !== null) {
      return;
    }

    this.foregroundTimerId = window.setInterval(() => {
      this.checkAndLogoutIfExpired();
    }, this.foregroundCheckIntervalInMs);
  }

  private stopForegroundGuard(): void {
    if (this.foregroundTimerId !== null) {
      window.clearInterval(this.foregroundTimerId);
      this.foregroundTimerId = null;
    }
  }

  /**
   * Evaluates the current route and session state before deciding to logout.
   */
  checkAndLogoutIfExpired(): void {
    if (!this.accountService.isLoggedIn()) {
      this.stopForegroundGuard();
      return;
    }

    if (this.isRouteSafe(this.router.url)) {
      // Safe routes are exempt from inactivity logout, so refresh the idle
      // marker instead of tearing down and re-registering activity listeners.
      this.activityService.setLastUserActivity();
      return;
    }

    if (this.activityService.isIdleDetected()) {
      this.logout();
    }
  }

  private isRouteSafe(currentUrl: string): boolean {
    return this.safeRoutes.some((route) => currentUrl.includes(route));
  }

  private logout(): void {
    const isLoggingOut = this.appService.getIsLoggingOut();
    if (isLoggingOut) {
      return;
    }

    this.stopForegroundGuard();
    this.logoutService.logout().subscribe({
      complete: () => {
        this.activityService.destroy();
        this.appNotificationService.showError(
          ErrorMessages.LOGGED_OUT_DUE_TO_INACTIVITY,
        );
      },
      error: () => {
        this.activityService.destroy();
      },
    });
  }
}
